import { Dispatch, Store } from "redux";
import { objectOverride, StoreProvider, Action, isType, actionCreatorFactory } from "diy/redux/helper";
const actionCreator = actionCreatorFactory('diy/module/section/');

import Modul from "../Modul";
import features = require('diy/util/features');
import $ = require('jquery');
import globalEvents = require('diy/util/globalEvents');
import { SectionKpiEventsTypes, emitKpiEvent } from "./tracking";
import {
    reducer as loadingMaskReducer,
    toggleLoadingMask,
    render as renderLoadingMask
} from './loadingMask';
import { init as groupsInit, render as groupsRender } from '../sectionContainer/index';

import {ModuleSectionState, AviaryOptions} from './state';
import {
    updateBackgroundImage, addGroup, delGroup, updateData,
    saveTransformedBackgroundImage, syncGroupsBackground
} from './data/service';
import {ModuleSectionData} from './data/payload';
import {unserializePayload} from './data/serialization';
import {setCurrentTab} from '../../module-action-bar/index';
import {AppState, MODULE_SECTION_STATE_KEY} from 'diy/redux/app/state';

// ==========
// PUBLIC API
// ==========
export {getAdvancedViewComponent} from './advancedTab';
export {getEditViewComponent} from './basicTab';
export {getGroupsViewComponent} from './groupsTab';
export {ModuleSectionData, BackgroundSettingsPayload} from './data/payload';
export {serializeBackgroundSettings, unserializeBackgroundSettings} from './data/serialization';
export {BackgroundSettings, ModuleSectionState} from './state';
export {updateData, addGroup, delGroup, updateBackgroundImage, saveTransformedBackgroundImage} from './data/service';

// =======
// ACTIONS
// =======
interface LoadPayload {
    moduleId: number;
    data: ModuleSectionData;
}
export const load = actionCreator<LoadPayload>("MODULE_SECTION_LOAD");

interface ToggleManageGroupsPayload {
    active: boolean;
}
const toggleManageGroupsRaw = actionCreator<ToggleManageGroupsPayload>(
    "MODULE_SECTION_TOGGLE_MANAGE_GROUPS"
);

interface ToggleElementsMenuPayload {
    visible: boolean;
}
export const toggleElementsMenuRaw = actionCreator<ToggleElementsMenuPayload>(
    "MODULE_SECTION_TOGGLE_ELEMENTS_MENU"
);

export function toggleElementsMenu(payload: ToggleElementsMenuPayload) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(setCurrentTab({nextTab: 0}));
        // We have to wait for the tab to render
        setTimeout(() => {
            dispatch(toggleElementsMenuRaw(payload));
        }, 500);
    };
}

interface ToggleBackgroundMenuPayload {
    visible: boolean;
}
export const toggleBackgroundMenu = actionCreator<ToggleBackgroundMenuPayload>(
    "MODULE_SECTION_TOGGLE_BACKGROUND_MENU"
);

interface OpenAviaryPayload {
    moduleId: number;
    origImageUrl: string;
    transformedImageUrl?: string;
    imageMediaType: string;
}
export const openAviary = actionCreator<OpenAviaryPayload>("MODULE_SECTION_OPEN_AVIARY");
export const closeAviary = actionCreator<{}>("MODULE_SECTION_CLOSE_AVIARY");

export function openAviarySection() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let ownState = getState().moduleSection;
        dispatch(openAviary({
            moduleId: ownState.moduleId,
            transformedImageUrl: ownState.background.transformedImageUrl,
            origImageUrl: ownState.background.origImageUrl,
            imageMediaType: ownState.background.imageMediaType
        }));
    };
}

export function stopEditing() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(closeManageGroupsIfActive());
    };
}

export function clickOutside() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let currentState = getState().moduleSection;

        if (currentState.manageGroupsActive) {
            dispatch(toggleManageGroups(false));
            dispatch(setCurrentTab({ nextTab: 0 }));
        } else {
            emitKpiEvent(SectionKpiEventsTypes.Close, 'outside');
            Modul.getInstance(currentState.moduleId).save();
        }
    };
}

export function closeManageGroupsIfActive() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let currentState = getState().moduleSection;

        if (currentState.manageGroupsActive) {
            dispatch(toggleManageGroups(false));
        }
    };
}

export function updatePayload(payload: ModuleSectionData) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let state = getState().moduleSection;

        dispatch(toggleLoadingMask({ active: true }));
        updateData<ModuleSectionData>(state.moduleId, payload).then((data) => {
            dispatch(load({ moduleId: state.moduleId, data }));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function addGroupAction() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let sectionId = getState().moduleSection.moduleId;
        emitKpiEvent(SectionKpiEventsTypes.AddGroup);
        dispatch(toggleLoadingMask({ active: true }));
        addGroup(sectionId).then((data) => {
            dispatch(load({ moduleId: sectionId, data: data }));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function deleteGroupAction(groupId: number) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let sectionId = getState().moduleSection.moduleId;
        emitKpiEvent(SectionKpiEventsTypes.DeleteGroup);
        dispatch(toggleLoadingMask({ active: true }));
        delGroup(groupId).then((data) => {
            dispatch(load({ moduleId: sectionId, data: data }));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function syncGroupsBackgroundTo(groupId: number) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let sectionId = getState().moduleSection.moduleId;
        emitKpiEvent(SectionKpiEventsTypes.BackgroundApplyOnAll);
        dispatch(toggleLoadingMask({ active: true }));
        syncGroupsBackground(groupId).then((data) => {
            dispatch(load({ moduleId: sectionId, data: data }));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function changeBackgroundImage(
    imageId: number,
    imageType: string,
    successCallback: () => void,
    errorCallback: (error?: string) => void
) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let state = getState().moduleSection;
        emitKpiEvent(SectionKpiEventsTypes.BackgroundImage);
        dispatch(toggleLoadingMask({ active: true }));
        updateBackgroundImage<ModuleSectionData>(
            state.moduleId, imageId, imageType, successCallback, errorCallback
        ).then((data) => {
            dispatch(load({ moduleId: state.moduleId, data }));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function saveBackgroundImage(imageDataUrl: string) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let state = getState().moduleSection;
        dispatch(closeAviary({}));
        dispatch(toggleLoadingMask({ active: true }));
        saveTransformedBackgroundImage<ModuleSectionData>(
            state.aviaryOptions.moduleId,
            imageDataUrl
        ).then(data => {
            dispatch(load({ moduleId: state.aviaryOptions.moduleId, data}));
            dispatch(toggleLoadingMask({ active: false }));
        }).catch(reason => {
            globalEvents.trigger("error.diy-editor", { "message": JSON.stringify(reason) });
        });
    };
}

export function supportsAddGroup(state: ModuleSectionState) {
    return features('sectionGroups') === true &&
        state.quantity.current > 0 &&
        state.configurableGroups.length > 0;
}

export function isToggleableElement(alias: string, state: ModuleSectionState) {
    if (!state.toggles) {
        return false;
    }
    return !!state.toggles.find(g => !!g.toggles.find(t => t.id === alias));
}

export function toggleManageGroups(active: boolean) {
    return function (dispatch: Dispatch, getState: () => AppState) {
        if (active) {
            emitKpiEvent(SectionKpiEventsTypes.ManageGroupsOpen);
        } else {
            emitKpiEvent(SectionKpiEventsTypes.ManageGroupsClose);
        }

        dispatch(toggleManageGroupsRaw({ active }));
    };
}

// =====
// STATE
// =====

const initialState = {} as ModuleSectionState;

// =======
// REDUCER
// =======
function reducer(
    state: ModuleSectionState = initialState,
    action: Action<any>
): ModuleSectionState {
    if (isType(action, load)) {
        return objectOverride(
            state,
            unserializePayload(action.payload.moduleId, action.payload.data
            ));
    }

    if (isType(action, toggleManageGroupsRaw)) {
        return objectOverride(state, {
            manageGroupsActive: action.payload.active
        });
    }

    if (isType(action, toggleElementsMenuRaw)) {
        return objectOverride(state, {
            elementsMenuVisible: action.payload.visible
        });
    }

    if (isType(action, toggleBackgroundMenu)) {
        return objectOverride(state, {
            backgroundMenuVisible: action.payload.visible
        });
    }

    if (isType(action, openAviary)) {
        return objectOverride(state, {
            backgroundMenuVisible: false,
            aviaryOpen: true,
            aviaryOptions: {
                moduleId: action.payload.moduleId,
                origImageUrl: action.payload.origImageUrl,
                transformedImageUrl: action.payload.transformedImageUrl,
                imageMediaType: action.payload.imageMediaType
            } as AviaryOptions
        });
    }

    if (isType(action, closeAviary)) {
        return objectOverride(state, {
            aviaryOpen: false,
            aviaryOptions: null
        });
    }

    return objectOverride(state, {
        loadingMask: loadingMaskReducer(state.loadingMask, action)
    });
}

// ==============
// INITIALIZATION
// ==============
let initialized = false;
export function init(storeProvider: StoreProvider) {
    if (!initialized) {
        storeProvider.addReducer(reducer, MODULE_SECTION_STATE_KEY);
        groupsInit(storeProvider);
        initialized = true;
    }
}

export function render(store: Store) {
    renderLoadingMask(store);
    groupsRender(store);
}

export function isModuleLimitReached() {
    return $("#cc-sa-sidebar").hasClass('modules-maximum');
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/section/index.ts
 **/